import type { ReactNode } from 'react';
import { Symbol } from './symbol';

interface ModalProps {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  /** Right-aligned footer actions (Cancel, then the one primary). */
  footer?: ReactNode;
  /** Panel width: sm 480 (confirmations), md 640, lg 860, xl 1080. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disables the close affordances while a request is in flight. */
  busy?: boolean;
  children: ReactNode;
  labelledBy?: string;
}

const WIDTH: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[860px]',
  xl: 'max-w-[1080px]',
};

/**
 * Centered dialog — design system v2 §11b: radius 24, elevation 4, jamun scrim.
 * Purely presentational: it renders whenever mounted and leaves open/close and
 * keyboard behaviour to the caller, so swapping a hand-rolled overlay for it
 * changes the look and nothing else.
 */
export function Modal({ title, description, onClose, footer, size = 'sm', busy = false, children }: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={() => !busy && onClose()}
        className="absolute inset-0 h-full w-full cursor-default bg-scrim motion-safe:animate-fadeIn"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative flex max-h-[calc(100dvh-48px)] w-full ${WIDTH[size]} flex-col overflow-hidden rounded-xl bg-surface-card shadow-elev-4 motion-safe:animate-pop`}
      >
        <div className="flex shrink-0 items-start gap-3 px-6 pb-2 pt-[22px]">
          <div className="min-w-0 flex-1">
            <h3 className="text-[19px] font-semibold leading-tight text-ink-900">{title}</h3>
            {description && <p className="mt-1 text-[14px] text-ink-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="-mr-2 -mt-1 grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full text-ink-500 transition-colors hover:bg-surface-2 hover:text-ink-900 disabled:opacity-50"
          >
            <Symbol name="close" size={20} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 px-6 pb-[22px] pt-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
